import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Modal } from '@safetyhub/ui';
import {
  EVENT_PERIOD_KEY_PREFIX,
  formatDateToLocal,
  type LegalObligationEvidenceView,
  type LegalObligationIntervalVariantView,
  type LegalObligationPendingResultView,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import {
  downloadLegalObligationEvidenceFile,
  getLegalObligationEvidences,
  legalObligationEvidenceQueryKeys,
  relinkLegalObligationEvidenceResult,
} from '../../lib/legalObligationEvidence';
import { describeMeasurementResult, EVIDENCE_KIND_LABEL, INSTANCE_STATUS_LABEL } from '../../lib/legalDocumentLabels';
import { useToastStore } from '../../stores/toast';
import ResultRelinkModal from './ResultRelinkModal';

export interface EvidenceListModalProps {
  open: boolean;
  companyId: string;
  siteId: string;
  instanceId: string;
  obligationTitle: string;
  /** `obligation.manage` — 있으면 「증빙 추가」 버튼을 보인다 */
  canManage: boolean;
  /** B+ — 결과표가 없는 측정 결과(연결 바꾸기의 선택지 · 서버 `pendingResults`). 생략하면 빈 배열 */
  pendingResults?: readonly LegalObligationPendingResultView[];
  /** 결과 이름을 찾는 변형 줄(`row.intervalVariants`) */
  resultVariants?: ReadonlyArray<Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>>;
  /** 연결을 바꾼 뒤 — 의무 목록(결과표 등록 판정·미연결 결과)을 다시 읽게 한다 */
  onResultRelinked?: () => void;
  onAttachMore: () => void;
  onClose: () => void;
}

/**
 * 인스턴스에 붙은 증빙 목록(9-d) — 종류·이행일·늦음·메모·붙인 사람. 「늦었나」는 서버가 기한과 대조해
 * 준 `late`를 그대로 그린다(화면이 다시 계산하지 않는다 — 기한의 정본은 서버의 박제 > live 규칙이다).
 *
 * 외부 파일은 **blob으로 받아** 앵커로 내려준다 — 그 purpose는 공개 다운로드 라우트가 막고 서버 인증이
 * Bearer 전용이라 `<a href>` 내비게이션은 401이다(`lib/legalObligationEvidence.ts` docblock).
 */
export default function EvidenceListModal({
  open,
  companyId,
  siteId,
  instanceId,
  obligationTitle,
  canManage,
  pendingResults = [],
  resultVariants = [],
  onResultRelinked,
  onAttachMore,
  onClose,
}: EvidenceListModalProps) {
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState<string | null>(null);
  /** B+ — 연결 바꾸기 대상 증빙 */
  const [relinkTarget, setRelinkTarget] = useState<LegalObligationEvidenceView | null>(null);
  const [relinkError, setRelinkError] = useState<string | null>(null);
  const relinkMutation = useMutation({
    mutationFn: ({ evidenceId, resultEntryId, reason }: { evidenceId: string; resultEntryId: string | null; reason: string }) =>
      relinkLegalObligationEvidenceResult(companyId, siteId, evidenceId, { resultEntryId, reason }),
    onSuccess: async (result) => {
      setRelinkTarget(null);
      showToast(
        result.instanceId !== instanceId
          ? '결과표 연결을 바꿨습니다. 문서가 그 측정이 든 기간으로 옮겨졌습니다.'
          : '결과표 연결을 바꿨습니다.',
        'success'
      );
      // 떠난 기간 · 옮겨 간 기간 둘 다 다시 읽는다(옮긴 기간의 상태가 파생으로 바뀐다)
      await queryClient.invalidateQueries({ queryKey: legalObligationEvidenceQueryKeys.evidences(companyId, siteId, instanceId) });
      if (result.instanceId !== instanceId) {
        await queryClient.invalidateQueries({
          queryKey: legalObligationEvidenceQueryKeys.evidences(companyId, siteId, result.instanceId),
        });
      }
      onResultRelinked?.();
    },
    onError: (err) => setRelinkError(apiErrorMessage(err) ?? '결과표 연결을 바꾸지 못했습니다.'),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: legalObligationEvidenceQueryKeys.evidences(companyId, siteId, instanceId),
    queryFn: () => getLegalObligationEvidences(companyId, siteId, instanceId),
    enabled: open,
  });

  const download = async (evidence: LegalObligationEvidenceView) => {
    if (!evidence.file || downloading) return;
    setDownloading(evidence.evidenceId);
    try {
      const blob = await downloadLegalObligationEvidenceFile(companyId, siteId, evidence.evidenceId);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = evidence.file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // 즉시 revoke하면 일부 브라우저(Firefox)가 다운로드를 취소한다 — 한 박자 뒤에 푼다(선례 `settings/Organization.tsx`)
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      showToast(apiErrorMessage(err) ?? '문서 파일을 내려받지 못했습니다.', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const instance = data?.instance;
  const evidences = data?.evidences ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="문서"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          {canManage && (
            <Button variant="secondary" onClick={onAttachMore}>
              문서 등록
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            닫기
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {instance ? (
            <>
              {' · '}
              {instance.periodKey}
              {' · '}
              <Badge tone={INSTANCE_STATUS_LABEL[instance.status].tone} size="sm" dot>
                {INSTANCE_STATUS_LABEL[instance.status].label}
              </Badge>
              <span className="ml-2 text-xs text-ink-500">기한 {formatDateToLocal(new Date(instance.deadline))}</span>
            </>
          ) : null}
        </p>

        {isLoading ? (
          <p className="text-sm text-ink-500" role="status">
            문서를 불러오는 중...
          </p>
        ) : error ? (
          <p role="alert" className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {apiErrorMessage(error) ?? '문서 목록을 불러오지 못했습니다.'}
          </p>
        ) : evidences.length === 0 ? (
          <p className="text-sm text-ink-500">아직 등록된 문서가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-ink-200" aria-label="문서 목록">
            {evidences.map((e) => (
              <li key={e.evidenceId} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink-900">
                      {EVIDENCE_KIND_LABEL[e.kind]}
                      {e.late && (
                        <Badge tone="amber" size="sm" className="ml-2">
                          늦은 이행
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-ink-500">
                      이행일 {formatDateToLocal(new Date(e.fulfilledAt))}
                      {e.attachedByName ? ` · ${e.attachedByName}` : ''}
                      {' · 기록 '}
                      {formatDateToLocal(new Date(e.attachedAt))}
                    </p>
                    {e.report && (
                      <Link
                        to={`/reports/${encodeURIComponent(e.report.reportId)}`}
                        className="mt-1 block truncate text-sm text-brand-600 hover:underline"
                      >
                        {e.report.title}
                      </Link>
                    )}
                    {e.note && <p className="mt-1 whitespace-pre-wrap text-sm text-ink-700">{e.note}</p>}
                    {e.result && (
                      <p className="mt-1 text-xs text-ink-700">
                        결과표 · {describeMeasurementResult(e.result, resultVariants)} 측정
                      </p>
                    )}
                    {canManage &&
                      e.kind !== 'NO_EVENT_IN_PERIOD' &&
                      !instance?.periodKey.startsWith(EVENT_PERIOD_KEY_PREFIX) &&
                      (e.result !== null || pendingResults.length > 0) && (
                        <button
                          type="button"
                          className="mt-1 text-xs font-medium text-brand-600 hover:underline"
                          onClick={() => {
                            setRelinkError(null);
                            setRelinkTarget(e);
                          }}
                        >
                          결과표 연결 바꾸기
                        </button>
                      )}
                  </div>
                  {e.file && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => download(e)}
                      disabled={downloading === e.evidenceId}
                    >
                      {downloading === e.evidenceId ? '받는 중...' : e.file.filename}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {relinkTarget && (
        <ResultRelinkModal
          key={relinkTarget.evidenceId}
          open
          current={relinkTarget.result}
          pendingResults={pendingResults}
          resultVariants={resultVariants}
          submitting={relinkMutation.isPending}
          errorMessage={relinkError}
          onSubmit={({ resultEntryId, reason }) => {
            setRelinkError(null);
            relinkMutation.mutate({ evidenceId: relinkTarget.evidenceId, resultEntryId, reason });
          }}
          onClose={() => setRelinkTarget(null)}
        />
      )}
    </Modal>
  );
}
